import React, { useCallback, useEffect, useMemo, useState } from "react";
import { calculateRelevanceFromScore } from "../utils";
import { SearchIcon } from "./Icons";
import { KeywordsModal } from "./keywordsModal";
import { Pagination } from "./Pagination";

// Download icon for export button
const DownloadIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="7,10 12,15 17,10"></polyline>
		<line x1="12" y1="15" x2="12" y2="3"></line>
	</svg>
);

// Chevron down icon for dropdown
const ChevronDownIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<polyline points="6,9 12,15 18,9"></polyline>
	</svg>
);

export const ResultsList = ({
	activeTab,
	data,
	fullData,
	selectedResults,
	onResultSelect,
	currentPage,
	totalPages,
	totalItems,
	itemsPerPage,
	onPageChange,
	onPageSizeChange,
	onShowUploadModal,
	onSelectAll,
	isAllSelected,
	isExporting,
	onExport,
	searchTerm,
	onSearchChange,
	isLoading = false,
	error = null,
	isPaginatedData = false,
	// Add these new props for exclude functionality
	onExcludeSelected,
	onAddToSearch,
	keywords = [], // Keywords data from domain search results
	searchQuery = "",
	onAddKeywordsToSearch,
	excludedItems = [], // Array of excluded item IDs
}) => {
	const [activeTooltip, setActiveTooltip] = useState(null);
	const [tooltipPosition, setTooltipPosition] = useState({
		top: true,
		left: 0,
	});
	const [isSearchVisible, setIsSearchVisible] = useState(false);
	const [activeHeaderTooltip, setActiveHeaderTooltip] = useState(null);
	const [isKeywordsModalOpen, setIsKeywordsModalOpen] = useState(false);

	const createFallbackItem = (index, type) => {
		if (type === "domains") {
			return {
				id: index + 1,
				domain: `Unknown Domain ${index + 1}`,
				categories: ["Unknown"],
				relevance: 0,
				reach: "N/A",
				marketRequest: 0,
				metrics: {
					viewability: 0,
					ctr: "0.00",
					vtr: "0.0",
					impressions: 0,
				},
			};
		} else {
			return {
				id: index + 1,
				keyword: `Unknown Keyword ${index + 1}`,
				associatedDomains: 0,
			};
		}
	};

	const extractMetrics = (item) => {
		const metrics = {};

		// Viewability
		if (typeof item.avgViewability === "number") {
			metrics.viewability = Math.round(item.avgViewability);
		} else if (item.metrics?.viewability) {
			metrics.viewability = Math.round(item.metrics.viewability);
		} else if (item.totalViewables && item.totalViewabilityMeasures) {
			metrics.viewability = Math.round(
				(item.totalViewables / item.totalViewabilityMeasures) * 100,
			);
		} else {
			metrics.viewability = 0;
		}

		// CTR (Click-Through Rate)
		if (typeof item.calculated_ctr === "number") {
			metrics.ctr = item.calculated_ctr.toFixed(4);
		} else if (typeof item.avgClickThroughRate === "number") {
			metrics.ctr = (item.avgClickThroughRate * 100).toFixed(4);
		} else if (item.metrics?.ctr) {
			metrics.ctr = item.metrics.ctr;
		} else if (item.totalCprClicks && item.totalImpressions) {
			metrics.ctr = (
				(item.totalCprClicks / item.totalImpressions) *
				100
			).toFixed(2);
		} else {
			metrics.ctr = "0.00";
		}

		// VTR (View-Through Rate)
		if (typeof item.avgVtr === "number") {
			metrics.vtr = item.avgVtr.toFixed(4);
		} else if (item.metrics?.vtr) {
			metrics.vtr = item.metrics.vtr;
		} else if (
			item.totalCompleteFirstOccurrence &&
			item.totalStartFirstOccurrence
		) {
			metrics.vtr = (
				(item.totalCompleteFirstOccurrence / item.totalStartFirstOccurrence) *
				100
			).toFixed(4);
		} else {
			metrics.vtr = "0.0";
		}

		// Impressions
		if (typeof item.totalImpressions === "number") {
			metrics.impressions = item.totalImpressions;
		} else if (item.metrics?.impressions) {
			metrics.impressions = item.metrics.impressions;
		} else if (item.totalCprClicks) {
			metrics.impressions = item.totalCprClicks * 50;
		} else {
			metrics.impressions = 0;
		}

		// Additional metrics for tooltips
		metrics.clicks = item.totalCprClicks || metrics.impressions / 50 || 0;
		metrics.spend = item.totalCprMarketDemandPartnerSpendInUsd || 0;
		metrics.bidGuidance = item.calculated_bid_guidance / 1000000 || 0;
		metrics.cpm = item.avgBidCpm / 1000000 || 0;
		return metrics;
	};

	const handleShowKeywords = () => {
		setIsKeywordsModalOpen(true);
	};

	const handleCloseKeywordsModal = () => {
		setIsKeywordsModalOpen(false);
	};

	const handleAddKeywordsToSearch = (selectedKeywords) => {
		if (onAddKeywordsToSearch) {
			onAddKeywordsToSearch(selectedKeywords);
		}
		setIsKeywordsModalOpen(false);
	};

	const ExportDropdown = ({ activeTab, isExporting, onExport }) => {
		const [isOpen, setIsOpen] = useState(false);

		// Get the complete dataset for export
		const getCompleteDataForExport = () => {
			// Priority: fullData prop > fallback to parent's onExport > current data
			if (fullData && Array.isArray(fullData)) {
				console.log(
					"✅ Using fullData prop for export:",
					fullData.length,
					"items",
				);
				return fullData;
			} else if (data && Array.isArray(data)) {
				console.warn(
					"⚠️ Using current page data for export (limited):",
					data.length,
					"items",
				);
				return data;
			} else {
				console.error("❌ No data available for export");
				return [];
			}
		};

		// Apply filters to the complete dataset
		const getFilteredCompleteData = () => {
			let completeData = getCompleteDataForExport();

			// Apply exclusion filters
			if (excludedItems && excludedItems.length > 0) {
				completeData = completeData.filter(
					(item) => !excludedItems.includes(item.id),
				);
			}

			// Apply search filters if searchTerm exists
			if (searchTerm && searchTerm.trim()) {
				completeData = completeData.filter((item) => {
					if (activeTab === "domains") {
						return (
							item.domain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
							item.domainName
								?.toLowerCase()
								.includes(searchTerm.toLowerCase()) ||
							(item.categories &&
								item.categories.some((cat) =>
									cat?.toLowerCase().includes(searchTerm.toLowerCase()),
								)) ||
							item.publisher?.toLowerCase().includes(searchTerm.toLowerCase())
						);
					} else if (activeTab === "keywords") {
						return item.keyword
							?.toLowerCase()
							.includes(searchTerm.toLowerCase());
					}
					return true;
				});
			}

			return completeData;
		};

		// Helper function to properly escape CSV values
		const escapeCsvValue = (value) => {
			if (value === null || value === undefined) return "";
			let stringValue = String(value);
			if (
				stringValue.includes(",") ||
				stringValue.includes("\n") ||
				stringValue.includes("\r") ||
				stringValue.includes('"')
			) {
				stringValue = stringValue.replace(/"/g, '""');
				return `"${stringValue}"`;
			}
			return stringValue;
		};

		// Helper function to format arrays for CSV
		const formatArrayForCsv = (arr) => {
			if (!Array.isArray(arr)) return escapeCsvValue(arr);
			const joinedValue = arr
				.filter((item) => item !== null && item !== undefined && item !== "")
				.join("; ");
			return escapeCsvValue(joinedValue);
		};

		const exportSummarized = () => {
			const completeData = getFilteredCompleteData();

			if (!completeData || completeData.length === 0) {
				alert("No data available for export. Please search first.");
				setIsOpen(false);
				return;
			}

			console.log(`📊 Exporting ${completeData.length} summarized records`);

			if (activeTab === "domains") {
				const headers = ["Domain", "Market Requests", "Score", "Categories"];
				const csvContent = [
					headers.join(","),
					...completeData.map((domain) => {
						return [
							escapeCsvValue(
								domain.domainName || domain.domain || "Unknown Domain",
							),
							escapeCsvValue(
								domain.totalCprMarketRequests || domain.marketRequest || 0,
							),
							escapeCsvValue(domain.score || domain.relevance || 0),
							formatArrayForCsv(domain.categories || []),
						].join(",");
					}),
				].join("\n");

				downloadCSV(
					csvContent,
					`domain_results_summarized_${completeData.length}_records.csv`,
				);
			} else {
				const headers = ["Keyword", "Associated Domains"];
				const csvContent = [
					headers.join(","),
					...completeData.map((keyword) => {
						return [
							escapeCsvValue(keyword.keyword || "Unknown Keyword"),
							escapeCsvValue(keyword.associatedDomains || 0),
						].join(",");
					}),
				].join("\n");

				downloadCSV(
					csvContent,
					`keyword_results_summarized_${completeData.length}_records.csv`,
				);
			}
			setIsOpen(false);
		};

		const exportDetailed = () => {
			const completeData = getFilteredCompleteData();

			if (!completeData || completeData.length === 0) {
				alert("No data available for export. Please search first.");
				setIsOpen(false);
				return;
			}

			console.log(`📊 Exporting ${completeData.length} detailed records`);

			if (activeTab === "domains") {
				const headers = [
					"Domain Name",
					"Domain Reach",
					"Score",
					"Categories",
					"Description",
					"Intended Audience",
					"Content Styles",
					"Geography",
					"IAB Media Subtype",
					"IAB Device Type",
					"Publisher Account IDs",
					"Is Direct",
					"Total CPR Clicks",
					"Total Impressions with Click Macro",
					"Total CPR Market Demand Partner Spend (USD)",
					"Total CPR Market Publisher Revenue (USD)",
					"Total CPR Market Requests",
					"Total CPR All Requests",
					"Total CPR Private Market Impressions",
					"Total Complete First Occurrence",
					"Total Start First Occurrence",
					"Total Viewables",
					"Total Viewability Measures",
					"Total Fills",
					"Total Non Zero Bids",
					"Total Impressions",
					"Total Deal Volume Deal Info",
					"Total Non Zero Bids Value (USD)",
					"Total Bid Spend (USD)",
					"Calculated Revenue Per Impression",
					"Calculated Cost Per Impression",
					"Calculated Bid Guidance",
					"Calculated CTR",
					"Average VTR",
					"Average Viewability",
					"Average Ox Win Rate",
					"Average Pub Win Rate",
					"Average Bid CPM",
				];

				const csvContent = [
					headers.join(","),
					...completeData.map((domain) => {
						return [
							escapeCsvValue(domain.domainName || "Unknown Domain"),
							escapeCsvValue(domain.domainReach || 0),
							escapeCsvValue(domain.score || 0),
							formatArrayForCsv(domain.categories || []),
							escapeCsvValue(domain.description || "N/A"),
							escapeCsvValue(domain.intendedAudience || "N/A"),
							formatArrayForCsv(domain.content_styles || []),
							formatArrayForCsv(domain.geography || []),
							formatArrayForCsv(domain.iabmediasubtype || []),
							formatArrayForCsv(domain.iabdevicetype || []),
							formatArrayForCsv(domain.publisheraccountid || []),
							formatArrayForCsv(domain.isDirect || []),
							escapeCsvValue(domain.totalCprClicks || 0),
							escapeCsvValue(domain.totalImpressionsWithClickMacro || 0),
							escapeCsvValue(domain.totalCprMarketDemandPartnerSpendInUsd || 0),
							escapeCsvValue(domain.totalCprMarketPublisherRevenueInUsd || 0),
							escapeCsvValue(domain.totalCprMarketRequests || 0),
							escapeCsvValue(domain.totalCprAllRequests || 0),
							escapeCsvValue(domain.totalCprPrivateMarketImpressions || 0),
							escapeCsvValue(domain.totalCompleteFirstOccurrence || 0),
							escapeCsvValue(domain.totalStartFirstOccurrence || 0),
							escapeCsvValue(domain.totalViewables || 0),
							escapeCsvValue(domain.totalViewabilityMeasures || 0),
							escapeCsvValue(domain.totalFills || 0),
							escapeCsvValue(domain.totalNonZeroBids || 0),
							escapeCsvValue(domain.totalImpressions || 0),
							escapeCsvValue(domain.totalDealVolumeDealInfo || 0),
							escapeCsvValue(domain.totalNonZeroBidsValueInUsd || 0),
							escapeCsvValue(domain.totalBidSpendInUsd || 0),
							escapeCsvValue(domain.calculated_revenueperimpression || 0),
							escapeCsvValue(domain.calculated_costperimpression || 0),
							escapeCsvValue(domain.calculated_bid_guidance || 0),
							escapeCsvValue(domain.calculated_ctr || 0),
							escapeCsvValue(domain.avgVtr || 0),
							escapeCsvValue(domain.avgViewability || 0),
							escapeCsvValue(domain.avgOxWinRate || 0),
							escapeCsvValue(domain.avgPubWinRate || 0),
							escapeCsvValue(domain.avgBidCpm || 0),
						].join(",");
					}),
				].join("\n");

				downloadCSV(
					csvContent,
					`domain_results_detailed_${completeData.length}_records.csv`,
				);
			} else {
				// Keywords detailed export
				const headers = [
					"Keyword",
					"Associated Domains",
					"Type",
					"Frequency",
					"Relevance",
				];
				const csvContent = [
					headers.join(","),
					...completeData.map((keyword) => {
						return [
							escapeCsvValue(keyword.keyword || "Unknown Keyword"),
							escapeCsvValue(keyword.associatedDomains || 0),
							escapeCsvValue(keyword.type || "N/A"),
							escapeCsvValue(keyword.frequency || "N/A"),
							escapeCsvValue(keyword.relevance || "N/A"),
						].join(",");
					}),
				].join("\n");

				downloadCSV(
					csvContent,
					`keyword_results_detailed_${completeData.length}_records.csv`,
				);
			}
			setIsOpen(false);
		};

		const downloadCSV = (content, filename) => {
			const BOM = "\uFEFF";
			const blob = new Blob([BOM + content], {
				type: "text/csv;charset=utf-8;",
			});
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);

			console.log(`✅ Export completed: ${filename}`);
		};

		// If no fullData provided, try to use parent's export function
		if (!fullData && onExport) {
			return (
				<button
					onClick={() => onExport("detailed")}
					className="btn-medium-secondary"
					disabled={isExporting}
					style={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
					}}
				>
					<DownloadIcon />
					{isExporting ? "Exporting..." : "Export Results"}
				</button>
			);
		}

		return (
			<div className="export-dropdown" style={{ position: "relative" }}>
				<button
					onClick={() => setIsOpen(!isOpen)}
					className="btn-medium-secondary"
					disabled={isExporting || !getCompleteDataForExport().length}
					style={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
					}}
				>
					<DownloadIcon />
					{isExporting ? "Exporting..." : `Export Results`}
					<ChevronDownIcon />
				</button>

				{isOpen && (
					<div
						className="dropdown-menu"
						style={{
							position: "absolute",
							top: "100%",
							right: 0,
							backgroundColor: "white",
							border: "1px solid #dee2e6",
							borderRadius: "4px",
							boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
							zIndex: 1000,
							minWidth: "160px",
							marginTop: "4px",
						}}
					>
						<button
							onClick={exportSummarized}
							className="dropdown-item"
							style={{
								width: "100%",
								padding: "8px 12px",
								border: "none",
								background: "none",
								textAlign: "left",
								cursor: "pointer",
								fontSize: "14px",
								color: "#212529",
							}}
							onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
							onMouseLeave={(e) =>
								(e.target.style.backgroundColor = "transparent")
							}
						>
							Export Summarized
						</button>
						<button
							onClick={exportDetailed}
							className="dropdown-item"
							style={{
								width: "100%",
								padding: "8px 12px",
								border: "none",
								background: "none",
								textAlign: "left",
								cursor: "pointer",
								fontSize: "14px",
								color: "#212529",
							}}
							onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
							onMouseLeave={(e) =>
								(e.target.style.backgroundColor = "transparent")
							}
						>
							Export Detailed
						</button>
					</div>
				)}
			</div>
		);
	};

	const sanitizeDomainItem = (item, index) => {
		try {
			const domain =
				item.domain || item.domainName || item.name || `Domain ${index + 1}`;
			const id = item.id || item.domainId || index + 1;

			let categories = [];
			if (Array.isArray(item.categories)) {
				categories = item.categories;
			} else if (Array.isArray(item.iab_category)) {
				categories = item.iab_category;
			} else if (typeof item.categories === "string") {
				categories = item.categories.split(",").map((cat) => cat.trim());
			} else if (item.category) {
				categories = Array.isArray(item.category)
					? item.category
					: [item.category];
			} else {
				categories = ["General"];
			}

			let relevance = 0;
			if (typeof item.score === "number") {
				relevance = calculateRelevanceFromScore(item.score);
			} else if (typeof item.relevance === "number") {
				relevance = Math.round(item.relevance);
			} else if (typeof item.relevanceScore === "number") {
				relevance = Math.round(item.relevanceScore);
			} else {
				relevance = 75;
			}

			let reach = "N/A";
			if (item.reach) {
				reach =
					typeof item.reach === "number" ? Math.round(item.reach) : item.reach;
			} else if (typeof item.domainReach === "number") {
				reach = Math.round(item.domainReach);
			} else if (typeof item.expectedDailyReach === "number") {
				reach = Math.round(item.expectedDailyReach);
			} else if (typeof item.avgReachPerDay === "number") {
				reach = Math.round(item.avgReachPerDay);
			}

			const metrics = extractMetrics(item);

			let marketRequest = "N/A";
			if (typeof item.marketRequest === "number") {
				marketRequest = item.marketRequest;
			} else if (
				item["totalcprmarket requests"] &&
				typeof item["totalcprmarket requests"] === "number"
			) {
				marketRequest = item["totalcprmarket requests"];
			} else if (
				item.totalcprmarketrequests &&
				typeof item.totalcprmarketrequests === "number"
			) {
				marketRequest = item.totalcprmarketrequests;
			} else if (
				item.totalCprMarketRequests &&
				typeof item.totalCprMarketRequests === "number"
			) {
				marketRequest = item.totalCprMarketRequests;
			} else if (
				item.total_cpr_market_requests &&
				typeof item.total_cpr_market_requests === "number"
			) {
				marketRequest = item.total_cpr_market_requests;
			} else if (
				item.totalcprmarketrequest &&
				typeof item.totalcprmarketrequest === "number"
			) {
				marketRequest = item.totalcprmarketrequest;
			} else if (item.isDirect === true || item.isDirect === "true") {
				marketRequest = "Direct";
			} else if (item.isAvailable === true || item.isAvailable === "true") {
				marketRequest = "Available";
			}

			return {
				...item,
				id,
				domain,
				categories,
				relevance,
				reach,
				marketRequest,
				metrics,
				safetyTier: item.safetyTier || item.safety_tier || "Standard",
				contentStyle: item.contentStyle || item.content_style || "General",
				intendedAudience:
					item.intendedAudience || item.targetAudience || "General",
				description: item.description || item.desc || `${domain} publisher`,
				publisherId: item.publisherId,
			};
		} catch (error) {
			console.error(`Error sanitizing domain item ${index}:`, error, item);
			return createFallbackItem(index, "domains");
		}
	};

	const sanitizeKeywordItem = (item, index) => {
		try {
			const keyword =
				item.keyword || item.text || item.query || `Keyword ${index + 1}`;
			const id = item.id || index + 1;

			let associatedDomains = 0;
			if (typeof item.associatedDomains === "number") {
				associatedDomains = item.associatedDomains;
			} else if (typeof item.domainCount === "number") {
				associatedDomains = item.domainCount;
			} else if (typeof item.frequency === "number") {
				associatedDomains = item.frequency;
			} else if (typeof item.score === "number") {
				associatedDomains = Math.floor(item.score / 1000);
			} else {
				associatedDomains = Math.floor(Math.random() * 50) + 10;
			}

			return {
				...item,
				id,
				keyword,
				associatedDomains,
			};
		} catch (error) {
			console.error(`Error sanitizing keyword item ${index}:`, error, item);
			return createFallbackItem(index, "keywords");
		}
	};

	const validateAndSanitizeData = useMemo(() => {
		if (!Array.isArray(data)) {
			return [];
		}

		return (
			data
				.map((item, index) => {
					if (!item || typeof item !== "object") {
						return createFallbackItem(index, activeTab);
					}

					if (activeTab === "domains") {
						return sanitizeDomainItem(item, index);
					}

					if (activeTab === "keywords") {
						return sanitizeKeywordItem(item, index);
					}

					return item;
				})
				.filter(Boolean)
				// Filter out excluded items
				.filter((item) => !excludedItems.includes(item.id))
		);
	}, [data, activeTab, excludedItems]);

	// FIXED: Remove local filtering logic - parent handles all filtering
	const filteredData = useMemo(() => {
		// ✅ FIXED: Always return data as-is when parent provides onSearchChange
		// Parent component should handle all filtering before passing data
		return validateAndSanitizeData;
	}, [validateAndSanitizeData]);

	const currentPageData = useMemo(() => {
		if (isPaginatedData) {
			return filteredData;
		}

		if (!currentPage || !itemsPerPage) {
			return filteredData;
		}

		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		return filteredData.slice(startIndex, endIndex);
	}, [filteredData, currentPage, itemsPerPage, isPaginatedData]);

	// ✅ SIMPLIFIED: Calculate effective pagination values with excluded items
	const effectiveTotalItems = totalItems
		? totalItems - (excludedItems?.length || 0)
		: filteredData?.length || 0;
	const effectiveTotalPages = Math.max(
		1,
		totalPages || Math.ceil(effectiveTotalItems / (itemsPerPage || 10)),
	);
	const effectiveCurrentPage = currentPage || 1;
	const effectiveItemsPerPage = itemsPerPage || 10;

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				!event.target.closest(".info-icon-wrapper") &&
				!event.target.closest(".metrics-tooltip")
			) {
				setActiveTooltip(null);
			}
		};

		if (activeTooltip) {
			document.addEventListener("click", handleClickOutside);
		}

		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, [activeTooltip]);

	// ✅ FIXED: Handle search like AppResultsList - pass to parent immediately
	useEffect(() => {
		if (onSearchChange && searchTerm !== undefined) {
			// Reset to page 1 when search term changes and notify parent
			if (onPageChange && searchTerm.trim() !== "") {
				onPageChange(1);
			}
		}
	}, [searchTerm, onSearchChange, onPageChange]);

	// Fixed tooltip position calculation
	const calculateTooltipPosition = (buttonElement) => {
		if (!buttonElement)
			return {
				top: true,
				left: 100,
				right: "auto",
				buttonTop: 0,
				buttonBottom: 100,
			};

		const rect = buttonElement.getBoundingClientRect();
		const viewportHeight = window.innerHeight;
		const viewportWidth = window.innerWidth;
		const tooltipHeight = 400;
		const tooltipWidth = 350;

		// Calculate if tooltip should show above or below
		const spaceBelow = viewportHeight - rect.bottom;
		const spaceAbove = rect.top;
		const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;

		// Calculate horizontal position
		let left = rect.left + rect.width / 2 - tooltipWidth / 2;
		const right = "auto";

		// Adjust horizontal position if tooltip would go off-screen
		if (left < 10) {
			left = 10;
		} else if (left + tooltipWidth > viewportWidth - 10) {
			left = viewportWidth - tooltipWidth - 10;
		}

		return {
			top: !showAbove,
			left: Math.max(10, left),
			right: right,
			buttonTop: rect.top,
			buttonBottom: rect.bottom,
			showAbove,
		};
	};

	const handleSelectAll = () => {
		if (onSelectAll) {
			onSelectAll(!isAllSelected);
		}
	};

	// ✅ FIXED: Handle search like AppResultsList
	const handleDomainSearch = (event) => {
		const value = event.target.value;

		if (onSearchChange) {
			// Pass search to parent and reset page
			onSearchChange(value);
			if (onPageChange && value.trim() !== (searchTerm || "").trim()) {
				onPageChange(1);
			}
		}
	};

	const clearSearch = () => {
		if (onSearchChange) {
			onSearchChange("");
			if (onPageChange) {
				onPageChange(1);
			}
		}
		setIsSearchVisible(false);
	};

	const formatNumber = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		if (typeof value === "number") {
			return value.toLocaleString();
		}
		if (typeof value === "string" && !isNaN(value)) {
			return parseInt(value).toLocaleString();
		}
		return value;
	};

	const formatPercentage = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		if (typeof value === "string" && value.includes("%")) return value;
		if (typeof value === "number") return `${value}%`;
		if (typeof value === "string" && !isNaN(value)) return `${value}%`;
		return value;
	};

	const handleTooltipToggle = (itemId, event) => {
		event.stopPropagation();
		if (activeTooltip === itemId) {
			setActiveTooltip(null);
		} else {
			const buttonElement =
				event.currentTarget.querySelector(".info-icon") || event.currentTarget;
			const position = calculateTooltipPosition(buttonElement);
			setTooltipPosition(position);
			setActiveTooltip(itemId);
		}
	};

	// Handle exclude selected button click
	const handleExcludeSelected = () => {
		if (onExcludeSelected && selectedResults.length > 0) {
			onExcludeSelected(selectedResults);
		}
	};

	// Handle add to search button click (for keywords)
	const handleAddToSearch = () => {
		if (onAddToSearch && selectedResults.length > 0) {
			const selectedItems = currentPageData.filter((item) =>
				selectedResults.includes(item.id),
			);
			onAddToSearch(selectedItems);
		}
	};

	// Fixed MetricsTooltip component
	const MetricsTooltip = ({ item, isVisible, position }) => {
		if (!isVisible || !item) return null;

		const tooltipStyle = {
			position: "fixed",
			left: `${position.left}px`,
			zIndex: 999999,
		};

		// Set top or bottom position based on calculation
		if (position.showAbove) {
			tooltipStyle.bottom = `${window.innerHeight - position.buttonTop + 10}px`;
		} else {
			tooltipStyle.top = `${position.buttonBottom + 10}px`;
		}

		return (
			<div className="metrics-tooltip" style={tooltipStyle}>
				<div className="tooltip-header">
					<h4>{item.domain || item.keyword || "Unknown"}</h4>
				</div>
				<div className="tooltip-content">
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Categories:</span>
						<span className="tooltip-metric-value tooltip-text-multiline">
							{Array.isArray(item.categories)
								? item.categories.join(", ")
								: item.categories || "N/A"}
						</span>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Safety Tier:</span>
						<span className="tooltip-metric-value">
							{item.safetyTier || "N/A"}
						</span>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Content Style:</span>
						<span className="tooltip-metric-value">
							{item.contentStyle || "N/A"}
						</span>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Reach:</span>
						<span className="tooltip-metric-value">
							{typeof item.reach === "number"
								? formatNumber(item.reach)
								: item.reach || "N/A"}
						</span>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Relevance:</span>
						<span className="tooltip-metric-value">
							<span
								className={`score ${item.relevance >= 85 ? "excellent" : item.relevance >= 78 ? "great" : item.relevance >= 65 ? "acceptable" : item.relevance >= 57 ? "poor" : "irrelevant"}`}
							>
								{item.relevance || 0}%
							</span>
						</span>
					</div>
					<div className="tooltip-metric-section">
						<h5>Performance Metrics</h5>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Viewability:</span>
							<span className="tooltip-metric-value">
								{formatPercentage(item.metrics?.viewability)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Click-Through Rate:</span>
							<span className="tooltip-metric-value">
								{formatPercentage(item.metrics?.ctr)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">View-Through Rate:</span>
							<span className="tooltip-metric-value">
								{formatPercentage(item.metrics?.vtr)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Total Impressions:</span>
							<span className="tooltip-metric-value">
								{formatNumber(item.metrics?.impressions)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">CPM:</span>
							<span className="tooltip-metric-value">
								${formatNumber(item.metrics?.cpm)}
							</span>
						</div>
						{item.metrics?.bidGuidance && (
							<div className="tooltip-metric-row">
								<span className="tooltip-metric-label">Bid Guidance:</span>
								<span className="tooltip-metric-value">
									${item.metrics.bidGuidance.toFixed(2)}
								</span>
							</div>
						)}
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Market Requests:</span>
						<span className="tooltip-metric-value">
							{typeof item.marketRequest === "number"
								? formatNumber(item.marketRequest)
								: item.marketRequest || "N/A"}
						</span>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Intended Audience:</span>
						<span className="tooltip-metric-value tooltip-text-multiline">
							{item.intendedAudience || "N/A"}
						</span>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Description:</span>
						<span className="tooltip-metric-value tooltip-text-multiline">
							{item.description || "N/A"}
						</span>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Publisher IDs:</span>
						<span className="tooltip-metric-value tooltip-text-multiline">
							{Array.isArray(item.publisherId)
								? item.publisherId.join(", ")
								: item.publisherId || "N/A"}
						</span>
					</div>
				</div>
			</div>
		);
	};

	// Fixed HeaderTooltip component
	const HeaderTooltip = ({ text, isVisible, triggerElement }) => {
		if (!isVisible || !triggerElement) return null;

		const rect = triggerElement.getBoundingClientRect();
		const tooltipStyle = {
			position: "fixed",
			left: `${rect.left + rect.width / 2 - 100}px`,
			bottom: `${window.innerHeight - rect.top + 10}px`,
			transform: "translateX(-50%)",
			zIndex: 999999,
		};

		return (
			<div className="header-tooltip" style={tooltipStyle}>
				{text}
				<div className="header-tooltip-arrow"></div>
			</div>
		);
	};

	if (isLoading) {
		return (
			<div className="results-container">
				<div className="results-section">
					<h2 className="section-title">
						{activeTab === "domains" ? "Domain Results" : "Keyword Results"}
					</h2>
					<div className="loading-state">
						<div className="loading-spinner"></div>
						<p>Loading results...</p>
					</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="results-container">
				<div className="results-section">
					<h2 className="section-title">
						{activeTab === "domains" ? "Domain Results" : "Keyword Results"}
					</h2>
					<div className="error-state">
						<div className="error-icon">❌</div>
						<h3>Error Loading Results</h3>
						<p>{error}</p>
						<button
							onClick={() => window.location.reload()}
							className="retry-button"
						>
							Retry
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="results-container">
			<div className="results-section">
				<h2 className="section-title">
					{activeTab === "domains" ? "Domain Results" : "Keyword Results"}
					<span className="results-count">
						({effectiveTotalItems || 0} results)
					</span>
				</h2>

				<div className="results-table-container">
					<table className="results-table">
						<thead>
							<tr>
								<th className="checkbox-column">
									<input
										type="checkbox"
										checked={isAllSelected}
										onChange={handleSelectAll}
										title="Select All"
									/>
								</th>
								{activeTab === "domains" ? (
									<>
										<th className="name-column">
											{isSearchVisible ? (
												<div className="search-header-container">
													<input
														type="text"
														placeholder="Search domains..."
														value={searchTerm || ""}
														onChange={handleDomainSearch}
														onFocus={(e) => {
															e.target.select();
														}}
														className="domain-search-input"
														autoFocus
													/>
													<button
														className="close-search-btn"
														onClick={() => {
															clearSearch();
															setIsSearchVisible(false);
														}}
														title="Close search"
													>
														✖
													</button>
												</div>
											) : (
												<div className="domain-header">
													<span className="header-text">Domain</span>
													<button
														className="search-toggle-btn"
														onClick={() => setIsSearchVisible(true)}
														title="Search domains"
													>
														<SearchIcon />
													</button>
												</div>
											)}
										</th>
										<th className="category-column">
											<span className="header-text">Categories</span>
										</th>
										<th className="relevance-column">
											<span className="header-text">Relevance</span>
										</th>
										<th className="market-request-column">
											<span className="header-text">Market Request</span>
										</th>
										<th className="impressions-column">
											<span className="header-text">Impressions</span>
										</th>
										<th className="viewability-column">
											<span className="header-text">Viewability</span>
										</th>
										<th className="ctr-column">
											<span className="header-text">CTR</span>
										</th>
										<th className="vtr-column">
											<span className="header-text">VTR</span>
										</th>
										<th className="reach-column">
											<div className="header-with-tooltip">
												<span className="header-text">Avg. Reach/Day</span>
												<div
													className="header-info-icon-wrapper"
													onMouseEnter={(e) => {
														setActiveHeaderTooltip({
															type: "reach",
															element: e.currentTarget,
														});
													}}
													onMouseLeave={() => setActiveHeaderTooltip(null)}
												>
													<button className="header-info-icon">i</button>
												</div>
											</div>
										</th>
										<th className="vtr-column">
											<span className="header-text">Composite score</span>
										</th>
									</>
								) : (
									<>
										<th className="name-column">
											<span className="header-text">Keyword</span>
										</th>
										<th className="impressions-column">
											<span className="header-text">Associated Domains</span>
										</th>
									</>
								)}
							</tr>
						</thead>
						<tbody>
							{currentPageData && currentPageData.length > 0
								? currentPageData.map((item) => (
										<tr
											key={item.id}
											className={`result-row ${selectedResults.includes(item.id) ? "selected" : ""}`}
										>
											<td className="checkbox-cell">
												<input
													type="checkbox"
													checked={selectedResults.includes(item.id)}
													onChange={() => onResultSelect(item.id)}
												/>
											</td>

											{activeTab === "domains" ? (
												<>
													<td
														className={`name-cell ${activeTooltip === item.id ? "tooltip-active" : ""}`}
													>
														<div className="domain-name-container">
															<span className="domain-name">{item.domain}</span>
															<div
																className="info-icon-wrapper"
																onClick={(e) => handleTooltipToggle(item.id, e)}
															>
																<button
																	className="info-icon"
																	title="View detailed metrics"
																>
																	i
																</button>
															</div>
														</div>
													</td>
													<td className="category-cell">
														<div className="categories">
															{Array.isArray(item.categories) ? (
																item.categories.map((category, index) => (
																	<span key={index} className="category-tag">
																		{category}
																	</span>
																))
															) : (
																<span className="category-tag">
																	{item.categories || "General"}
																</span>
															)}
														</div>
													</td>
													<td className="relevance-cell">
														<span
															className={`score ${item.relevance >= 85 ? "excellent" : item.relevance >= 78 ? "great" : item.relevance >= 65 ? "acceptable" : item.relevance >= 57 ? "poor" : "irrelevant"}`}
														>
															{item.relevance}%
														</span>
													</td>
													<td className="market-request-cell">
														{typeof item.marketRequest === "number"
															? formatNumber(item.marketRequest)
															: item.marketRequest || "N/A"}
													</td>
													<td className="impressions-cell">
														{formatNumber(item.metrics?.impressions)}
													</td>
													<td className="viewability-cell">
														{formatPercentage(item.metrics?.viewability)}
													</td>
													<td className="ctr-cell">
														{formatPercentage(item.metrics?.ctr)}
													</td>
													<td className="vtr-cell">
														{formatPercentage(item.metrics?.vtr)}
													</td>
													<td className="reach-cell">
														{typeof item.reach === "number"
															? formatNumber(item.reach)
															: item.reach}
													</td>
													<td className="impressions-cell">
														{formatNumber(item.compositeScore)}
													</td>
												</>
											) : (
												<>
													<td className="name-cell">{item.keyword}</td>
													<td className="impressions-cell">
														{formatNumber(item.associatedDomains)}
													</td>
												</>
											)}
										</tr>
									))
								: console.log("🔍 ResultsList - Empty Results Debug:", {
										hasCurrentPageData: !!currentPageData,
										currentPageDataLength: currentPageData?.length,
										hasError: !!error,
										errorValue: error,
										isLoading: isLoading,
										searchTerm: searchTerm,
										dataLength: data?.length,
										filteredDataLength: filteredData?.length,
										message:
											"This should only show when there is NO error and NO loading",
									}) || (
										<tr className="empty-results-row">
											<td
												colSpan={activeTab === "domains" ? 11 : 3}
												className="empty-results-cell"
											>
												<div className="empty-results-content">
													<h3>No Results Found</h3>
													<p>
														{searchTerm
															? `No results match "${searchTerm}". Try adjusting your search.`
															: "No data available. Try running a search or adjusting your filters."}
													</p>
													{searchTerm && (
														<button
															onClick={clearSearch}
															className="clear-search-button"
														>
															Clear Search
														</button>
													)}
												</div>
											</td>
										</tr>
									)}
						</tbody>
					</table>
				</div>

				{/* Always show pagination */}
				<Pagination
					currentPage={effectiveCurrentPage}
					totalPages={effectiveTotalPages}
					totalItems={effectiveTotalItems}
					itemsPerPage={effectiveItemsPerPage}
					onPageChange={onPageChange}
					onPageSizeChange={onPageSizeChange}
					loading={false}
					showJumpToPage={true}
					showPageSizeSelector={true}
					showResultsInfo={true}
					forceShow={effectiveTotalItems > 0}
					excludedCount={excludedItems.length} // ✅ SIMPLIFIED: Just pass the count
				/>

				<div className="action-buttons">
					<>
						<button
							type="button"
							className="exclude-btn"
							disabled={
								selectedResults.length === 0 ||
								!currentPageData ||
								currentPageData.length === 0
							}
							onClick={handleExcludeSelected}
						>
							Exclude Selected ({selectedResults.length})
						</button>
						<button
							type="button"
							className="keywords-btn"
							onClick={handleShowKeywords}
						>
							Show Keywords ({keywords.length})
						</button>
					</>
					<ExportDropdown
						activeTab={activeTab}
						isExporting={isExporting}
						onExport={onExport}
					/>
				</div>
			</div>

			{/* Render tooltips outside the table to avoid clipping */}
			{activeTooltip && (
				<MetricsTooltip
					item={currentPageData.find((item) => item.id === activeTooltip)}
					isVisible={activeTooltip !== null}
					position={tooltipPosition}
				/>
			)}

			{activeHeaderTooltip && activeHeaderTooltip.type === "reach" && (
				<HeaderTooltip
					text="This is a domain level reach without any filter restrictions"
					isVisible={true}
					triggerElement={activeHeaderTooltip.element}
				/>
			)}

			<KeywordsModal
				isOpen={isKeywordsModalOpen}
				onClose={handleCloseKeywordsModal}
				keywords={keywords}
				onAddToSearch={handleAddKeywordsToSearch}
				//onExportKeywords={handleExportKeywords}
				searchQuery={searchQuery}
			/>

			<style jsx>{`
                .results-section {
                    overflow: visible;
                    position: relative;
                    z-index: 1;
                }

                .section-title {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 20px;
                    font-size: 18px;
                    font-weight: 600;
                    color: #212529;
                }

                .results-count {
                    font-size: 14px;
                    color: #666;
                    font-weight: normal;
                }

                .results-table-container {
                    overflow-x: auto;
                    overflow-y: visible;
                    position: static;
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    background-color: white;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    margin-bottom: 20px;
                }

                .results-table {
                    position: static;
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: auto;
                    font-size: 14px;
                    min-width: 1000px; /* Increased minimum width for better header visibility */
                }

                /* Desktop column widths - increased for better header visibility */
                .results-table .checkbox-column { width: 45px; min-width: 45px; }
                .results-table .name-column { width: 220px; min-width: 180px; }
                .results-table .category-column { width: 200px; min-width: 160px; }
                .results-table .relevance-column { width: 90px; min-width: 80px; }
                .results-table .reach-column { width: 140px; min-width: 120px; }
                .results-table .market-request-column { width: 140px; min-width: 110px; }
                .results-table .viewability-column { width: 100px; min-width: 85px; }
                .results-table .ctr-column { width: 90px; min-width: 70px; }
                .results-table .vtr-column { width: 90px; min-width: 70px; }
                .results-table .impressions-column { width: 120px; min-width: 100px; }

                .results-table th {
                    background-color: #f8f9fa;
                    border-bottom: 2px solid #dee2e6;
                    padding: 12px 8px;
                    text-align: left;
                    font-weight: 600;
                    font-size: 12px;
                    color: #495057;
                    white-space: nowrap;
                    position: static;
                    overflow: hidden;
                }

                .results-table td {
                    padding: 12px 8px;
                    border-bottom: 1px solid #e9ecef;
                    vertical-align: middle;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    position: static;
                    overflow: hidden;
                }

                .checkbox-cell, .checkbox-column {
                    text-align: center;
                    padding: 8px;
                }

                .relevance-cell, .relevance-column,
                .viewability-cell, .viewability-column,
                .ctr-cell, .ctr-column,
                .vtr-cell, .vtr-column {
                    text-align: center;
                }

                .impressions-cell, .impressions-column,
                .reach-cell, .reach-column {
                    text-align: center;
                }

                .market-request-cell, .market-request-column {
                    text-align: center;
                }

                .domain-name-container {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    position: static;
                    min-width: 0;
                }

                .domain-name {
                    font-weight: 500;
                    color: #212529;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    flex: 1;
                }

                .categories {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 4px;
                    align-items: center;
                }

                .category-tag {
                    display: inline-block;
                    padding: 2px 6px;
                    background-color: #e9ecef;
                    color: #495057;
                    border-radius: 3px;
                    font-size: 10px;
                    font-weight: 500;
                    white-space: nowrap;
                    max-width: 80px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .score {
                    display: inline-block;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-weight: 600;
                    font-size: 12px;
                    background-color: #f8f9fa;
                }

                .score.excellent { 
    background-color: #d4edda; 
    color: #155724; 
}
.score.great { 
    background-color: #d1ecf1; 
    color: #0c5460; 
}
.score.acceptable { 
    background-color: #fff3cd; 
    color: #856404; 
}
.score.poor { 
    background-color: #f8d7da; 
    color: #721c24; 
}
.score.irrelevant { 
    background-color: #f5f5f5; 
    color: #6c757d; 
}

                .result-row {
                    transition: background-color 0.2s ease;
                }

                .result-row:hover {
                    background-color: #f8f9fa;
                }

                .result-row.selected {
                    background-color: #e3f2fd;
                }

                .action-buttons {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                    margin-top: 20px;
                    padding: 16px;
                    background-color: #f8f9fa;
                    border-radius: 8px;
                    border: 1px solid #dee2e6;
                }

                .btn-medium-primary {
                    padding: 8px 16px;
                    background-color: #0076BD;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .btn-medium-primary:hover:not(:disabled) {
                    background-color: #005a94;
                }

                .btn-medium-secondary {
                    padding: 8px 16px;
                    background-color: #1FBAB0;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .btn-medium-secondary:hover:not(:disabled) {
                    background-color: #199f98;
                }

                .btn-medium-secondary:disabled {
                    background-color:rgb(24, 176, 179);
                    cursor: not-allowed;
                }

                .exclude-btn {
                    padding: 8px 16px;
                    background-color: #cb2816ff;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .exclude-btn:hover:not(:disabled) {
                    background-color: #f2a09cff;
                }

                .exclude-btn:disabled {
                    background-color: #f2a09cff;
                    cursor: not-allowed;
                }

                .keywords-btn {
                    padding: 8px 16px;
                    background-color: #005a94;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .name-cell {
                    white-space: normal;
                    line-height: 1.3;
                }

                .category-cell {
                    white-space: normal;
                    line-height: 1.4;
                }

                .relevance-cell,
                .viewability-cell,
                .ctr-cell,
                .vtr-cell {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    font-weight: 500;
                }

                .impressions-cell,
                .reach-cell {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    font-weight: 500;
                    color: #495057;
                }

                .market-request-cell {
                    font-weight: 500;
                }

                .checkbox-cell input[type="checkbox"],
                .checkbox-column input[type="checkbox"] {
                    width: 16px;
                    height: 16px;
                    cursor: pointer;
                }

                .loading-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    text-align: center;
                }

                .loading-spinner {
    width: 30px;
    height: 30px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #0076BD;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 15px auto;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

                .error-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    text-align: center;
                    color: #dc3545;
                }

                .error-message {
    color: #dc3545;
    margin: 10px 0;
    padding: 10px;
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.4;
}

.error-icon {
    font-size: 48px;
    margin-bottom: 15px;
    color: #dc3545;
}

                .retry-button {
                    padding: 10px 20px;
                    background-color: #dc3545;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    margin-top: 15px;
                }

                .retry-button:hover {
                    background-color: #c82333;
                }

                .empty-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    text-align: center;
                    color: #666;
                }

                .clear-search-button {
                    padding: 8px 16px;
                    background-color: #6c757d;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    margin-top: 15px;
                }

                .clear-search-button:hover {
                    background-color: #545b62;
                }

                .domain-search-input {
                    width: 100%;
                    padding: 3px 20px 3px 6px;
                    border: 1px solid #d1d5db;
                    border-radius: 3px;
                    font-size: 11px;
                    background-color: white;
                    transition: border-color 0.2s;
                    height: 22px;
                    line-height: 1;
                }

                .domain-search-input:focus {
                    outline: none;
                    border-color: #0076BD;
                    box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
                }

                .close-search-btn {
                    position: absolute;
                    right: 3px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    font-size: 14px;
                    line-height: 1;
                    padding: 1px 2px;
                    border-radius: 2px;
                    transition: color 0.2s;
                    height: 16px;
                    width: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .close-search-btn:hover {
                    color: #374151;
                    background-color: #f3f4f6;
                }

                .info-icon-wrapper {
                    position: static;
                    display: inline-flex;
                    flex-shrink: 0;
                }

                .info-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    background-color: #6c757d;
                    color: white;
                    border: none;
                    font-size: 11px;
                    font-weight: bold;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .info-icon:hover {
                    background-color: #495057;
                    color: white;
                }

                .header-with-tooltip {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    justify-content: center;
                    position: static;
                    flex-wrap: nowrap;
                }

                .header-info-icon-wrapper {
                    position: static;
                    display: inline-flex;
                    flex-shrink: 0;
                }

                .header-info-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 14px;
                    height: 14px;
                    border-radius: 50%;
                    background-color: #6c757d;
                    color: white;
                    border: none;
                    font-size: 10px;
                    font-weight: bold;
                    cursor: pointer;
                    transition: all 0.2s;
                    flex-shrink: 0;
                }

                .header-info-icon:hover {
                    background-color: #495057;
                }

                /* Header text wrapper for better control */
                .header-text {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: block;
                }

                /* Domain header improvements */
                .domain-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                    min-width: 0;
                }

                .search-toggle-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    font-size: 12px;
                    padding: 4px;
                    border-radius: 3px;
                    transition: background-color 0.2s;
                    flex-shrink: 0;
                    margin-left: 4px;
                }

                .search-toggle-btn:hover {
                    background-color: rgba(0,0,0,0.1);
                }

                .search-header-container {
                    position: relative;
                    display: flex;
                    align-items: center;
                    width: 100%;
                }

                /* Fixed header tooltip styles */
                .header-tooltip {
                    background: #333;
                    color: white;
                    padding: 8px 12px;
                    border-radius: 6px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
                    white-space: nowrap;
                    font-size: 12px;
                    pointer-events: none;
                    min-width: 200px;
                    text-align: center;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                }

                .header-tooltip-arrow {
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 6px solid transparent;
                    border-right: 6px solid transparent;
                    border-top: 6px solid #333;
                }

                /* Fixed metrics tooltip styles */
                .metrics-tooltip {
                    background: #0076BD;
                    color: white;
                    border-radius: 8px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
                    min-width: 350px;
                    max-width: 400px;
                    max-height: 400px;
                    overflow-y: auto;
                    pointer-events: auto;
                }

                .tooltip-header {
                    padding: 12px 16px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                    border-radius: 8px 8px 0 0;
                }

                .tooltip-header h4 {
                    margin: 0;
                    font-size: 14px;
                    font-weight: 600;
                    color: white;
                }

                .tooltip-content {
                    padding: 16px;
                }

                .tooltip-metric-section {
                    margin-top: 16px;
                }

                .tooltip-metric-section h5 {
                    margin: 0 0 10px 0;
                    font-size: 13px;
                    font-weight: 600;
                    color: white;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                    padding-bottom: 6px;
                }

                .tooltip-metric-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    padding: 6px 0;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                    gap: 8px;
                }

                .tooltip-metric-row:last-child {
                    border-bottom: none;
                }

                .tooltip-metric-label {
                    font-weight: 400;
                    color: rgba(255, 255, 255, 0.9);
                    font-size: 12px;
                    flex-shrink: 0;
                    min-width: 120px;
                }

                .tooltip-metric-value {
                    font-weight: 500;
                    color: white;
                    font-size: 12px;
                }

                .tooltip-text-multiline {
                    white-space: normal;
                    word-wrap: break-word;
                    line-height: 1.4;
                    max-width: 200px;
                    text-align: left;
                }

                /* Responsive improvements */
                @media (max-width: 1200px) {
                    .results-table .name-column { width: 180px; }
                    .results-table .category-column { width: 160px; }
                    .results-table .reach-column { width: 110px; }
                    .results-table .market-request-column { width: 110px; }
                    .results-table {
                        min-width: 900px;
                    }
                }

                @media (max-width: 992px) {
                    .results-table {
                        font-size: 12px;
                        min-width: 800px;
                    }
                    
                    .results-table th,
                    .results-table td {
                        padding: 8px 6px;
                    }
                    
                    .results-table .name-column { width: 160px; }
                    .results-table .category-column { width: 140px; }
                    .results-table .reach-column { width: 100px; }
                    .results-table .market-request-column { width: 100px; }
                    .results-table .viewability-column { width: 80px; }
                    .results-table .ctr-column { width: 70px; }
                    .results-table .vtr-column { width: 70px; }
                    .results-table .impressions-column { width: 90px; }
                    
                    .header-text {
                        font-size: 11px;
                    }
                    
                    .header-with-tooltip {
                        flex-direction: column;
                        gap: 2px;
                        align-items: center;
                    }
                }

                @media (max-width: 768px) {
                    .results-table {
                        font-size: 11px;
                        min-width: 700px;
                    }
                    
                    .results-table th,
                    .results-table td {
                        padding: 6px 4px;
                    }
                    
                    .results-table .name-column { width: 140px; }
                    .results-table .category-column { width: 120px; }
                    .results-table .relevance-column { width: 70px; }
                    .results-table .reach-column { width: 90px; }
                    .results-table .market-request-column { width: 90px; }
                    .results-table .viewability-column { width: 70px; }
                    .results-table .ctr-column { width: 60px; }
                    .results-table .vtr-column { width: 60px; }
                    .results-table .impressions-column { width: 80px; }
                    
                    .metrics-tooltip {
                        min-width: 90vw;
                        max-width: 90vw;
                        left: 5vw !important;
                        right: auto !important;
                    }
                    
                    .header-tooltip {
                        width: 250px;
                        white-space: normal;
                        text-align: center;
                    }

                    .tooltip-text-multiline {
                        max-width: none;
                        text-align: left;
                    }

                    .tooltip-metric-label {
                        min-width: 100px;
                        font-size: 11px;
                    }

                    .tooltip-metric-value {
                        font-size: 11px;
                    }
                    
                    .header-text {
                        font-size: 10px;
                    }
                    
                    .search-toggle-btn {
                        padding: 2px;
                        font-size: 10px;
                    }
                    
                    .domain-header {
                        flex-direction: column;
                        gap: 2px;
                        align-items: center;
                    }

                    .empty-results-row {
                        background-color: #f8f9fa;
                    }

                    .empty-results-cell {
                        padding: 60px 20px !important;
                        text-align: center;
                        border: none;
                        vertical-align: middle;
                    }

                    .empty-results-content {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        color: #666;
                    }

                    .empty-results-content .empty-icon {
                        font-size: 48px;
                        margin-bottom: 20px;
                    }

                    .empty-results-content h3 {
                        margin: 0 0 15px 0;
                        font-size: 18px;
                        color: #333;
                    }

                    .empty-results-content p {
                        margin: 0 0 15px 0;
                        color: #666;
                        line-height: 1.5;
                        max-width: 400px;
                    }
                }

                @media (max-width: 480px) {
                    .results-table {
                        min-width: 600px;
                        font-size: 10px;
                    }
                    
                    .results-table th,
                    .results-table td {
                        padding: 4px 2px;
                    }
                    
                    .results-table .name-column { width: 120px; }
                    .results-table .category-column { width: 100px; }
                    .results-table .relevance-column { width: 60px; }
                    .results-table .reach-column { width: 80px; }
                    .results-table .market-request-column { width: 80px; }
                    .results-table .viewability-column { width: 60px; }
                    .results-table .ctr-column { width: 50px; }
                    .results-table .vtr-column { width: 50px; }
                    .results-table .impressions-column { width: 70px; }
                    
                    .section-title {
                        font-size: 16px;
                        margin-bottom: 15px;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 4px;
                    }
                    
                    .results-count {
                        font-size: 12px;
                    }
                    
                    .action-buttons {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 8px;
                    }
                    
                    .btn-medium-primary,
                    .btn-medium-secondary {
                        width: 100%;
                        text-align: center;
                    }
                    
                    .header-text {
                        font-size: 9px;
                        line-height: 1.2;
                    }
                    
                    .category-tag {
                        font-size: 8px;
                        padding: 1px 4px;
                        max-width: 60px;
                    }
                }
            `}</style>
		</div>
	);
};
